// context/FeatureModalContext.tsx
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { FeatureSelectionTab } from '../interface/featureSelection';
import { IInAppNotification } from '../interface';
import { useFeatureContext } from './FeatureContext';
import FeatureModal from '../components/informationPopup/featureModal';

interface FeatureModalState {
  visible: boolean;
  featureToEnable?: FeatureSelectionTab;
  notification?: IInAppNotification;
  isRestrictedUserModal: boolean;
  onConfirmCallback?: (
    featureToEnable?: FeatureSelectionTab,
    notification?: IInAppNotification,
  ) => void;
}

interface FeatureModalContextType {
  showFeatureModal: (
    featureToEnable: FeatureSelectionTab,
    notification?: IInAppNotification,
    onConfirm?: (featureToEnable?: FeatureSelectionTab, notification?: IInAppNotification) => void,
  ) => void;
  showRestrictedUserModal: (onConfirm?: () => void) => void;
  hideModal: () => void;
  isModalVisible: boolean;
}

const FeatureModalContext = createContext<FeatureModalContextType | undefined>(undefined);

export const FeatureModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { features, updateFeatures } = useFeatureContext();
  const [modalState, setModalState] = useState<FeatureModalState>({
    visible: false,
    isRestrictedUserModal: false,
  });

  const showFeatureModal = useCallback(
    (
      featureToEnable: FeatureSelectionTab,
      notification?: IInAppNotification,
      onConfirm?: (
        featureToEnable?: FeatureSelectionTab,
        notification?: IInAppNotification,
      ) => void,
    ) => {
      setModalState({
        visible: true,
        featureToEnable,
        notification,
        isRestrictedUserModal: false,
        onConfirmCallback: onConfirm,
      });
    },
    [],
  );

  const showRestrictedUserModal = useCallback((onConfirm?: () => void) => {
    setModalState({
      visible: true,
      isRestrictedUserModal: true,
      onConfirmCallback: onConfirm,
    });
  }, []);

  const hideModal = useCallback(() => {
    setModalState({
      visible: false,
      isRestrictedUserModal: false,
    });
  }, []);

  const handleConfirm = (
    featureToEnable?: FeatureSelectionTab,
    notification?: IInAppNotification,
  ) => {
    if (modalState.isRestrictedUserModal) {
      modalState.onConfirmCallback?.();
    } else if (featureToEnable) {
      updateFeatures({
        ...features,
        [featureToEnable]: true,
      } as { [key: string]: boolean });

      modalState.onConfirmCallback?.(featureToEnable, notification);
    }

    hideModal();
  };

  const contextValue: FeatureModalContextType = React.useMemo(
    () => ({
      showFeatureModal,
      showRestrictedUserModal,
      hideModal,
      isModalVisible: modalState.visible,
    }),
    [showFeatureModal, showRestrictedUserModal, hideModal, modalState.visible],
  );

  return (
    <FeatureModalContext.Provider value={contextValue}>
      {children}
      <FeatureModal
        visible={modalState.visible}
        featureToEnable={modalState.featureToEnable}
        notification={modalState.notification}
        isRestrictedUserModal={modalState.isRestrictedUserModal}
        onClose={hideModal}
        onConfirm={handleConfirm}
      />
    </FeatureModalContext.Provider>
  );
};

export const useFeatureModal = (): FeatureModalContextType => {
  const context = useContext(FeatureModalContext);
  if (!context) {
    throw new Error('useFeatureModal must be used within a FeatureModalProvider');
  }
  return context;
};
